import type { DispatchTier, ResolvedTaskV1Annotation, ResolvedTaskV2Annotation } from '@sparkai/shared-types'

import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { sortBy } from 'lodash'

import { apiClient } from '../../apiClient'

type ResolvedTaskMetricsImage = {
  location: string
  thumbnailSmLocation: string
  thumbnailLgLocation: string
}

export type ResolvedTaskMetricsMedia = ResolvedTaskMetricsImage & {
  mediaId: string | null
  mimeType: string
}

type ResolvedTaskMetricsBase = {
  taskId: string
  engagementId: string
  createdDate: string
  priority: 'high' | 'low'
  ttc: number | null
  missedTargetTime: boolean | null
  fasterResponse: boolean
  dispatchTier: DispatchTier | null
  engagementState: string
  engagementStateDetail: string | null
  systemId: string | null
  response: string
  accuracy: number | null
  evaluation: 'audited' | null
  totalComments: number
  totalUnreadComments: number
  operator: {
    id: string
    name: string | null
    email: string
    avatarUrl: string | null
  }
  program: {
    id: string
    code: string
    displayName: string | null
    color: string | null
    iconType: string | null
    slaResponseTime: number | null
    targetSpeed: number | null
    imgUrls: ResolvedTaskMetricsImage[]
  }
  media: ResolvedTaskMetricsMedia[]
}

export type ResolvedTaskMetrics =
  | (ResolvedTaskMetricsBase & { version: 1; annotations: ResolvedTaskV1Annotation[] })
  | (ResolvedTaskMetricsBase & { version: 2; annotations: ResolvedTaskV2Annotation[] })

export type ResolvedTasksResponse = {
  total: number
  resolvedTasks: ResolvedTaskMetrics[]
}

export const RESOLVED_TASK_METRICS_ORDER_BY = [
  'createdDate',
  'priority',
  'programCode',
  'operatorName',
  'systemId',
  'taskId',
  'ttc',
  'accuracy',
] as const

export type ResolvedTaskMetricsOrderBy = (typeof RESOLVED_TASK_METRICS_ORDER_BY)[number]

export type ResolvedTasksRequest = {
  operatorId?: string
  programId?: string
  programCode?: string
  cursor: number
  limit: number
  orderBy: ResolvedTaskMetricsOrderBy
  sortDescending: boolean
  startCreatedDate?: string
  endCreatedDate?: string
  startClosedDate?: string
  endClosedDate?: string
  startTimeToClose?: string
  endTimeToClose?: string
  startAccuracy?: string
  endAccuracy?: string
  isConsensus?: boolean
  isUnderRelativeTTC?: boolean
  isAudited?: boolean
  stateDetail?: string | string[]
  voiderId?: string
  communication?: 'unread_only' | 'with_comments'
  dateRangeTimezone?: string
  useSelectedTimezoneContext?: boolean
  coordinates?: {
    latitude: string
    longitude: string
    radiusValue: number
    radiusUnit: 'mile' | 'feet'
  }
}

const toMilliseconds = (seconds?: string) =>
  seconds === undefined ? undefined : Number.parseInt(seconds, 10) * 1000

const toStateDetailList = (stateDetail?: string | string[]) =>
  typeof stateDetail === 'string' ? stateDetail.split(',').filter(Boolean) : stateDetail

const request = async (params: ResolvedTasksRequest, pageParam: number): Promise<ResolvedTasksResponse> => {
  const queryParams = { ...params }
  delete queryParams.useSelectedTimezoneContext
  const { data } = await apiClient.get<ResolvedTasksResponse>(`/int/v3/tasks/resolved/metrics`, {
    params: {
      ...queryParams,
      cursor: pageParam,
      startTimeToClose: toMilliseconds(queryParams.startTimeToClose),
      endTimeToClose: toMilliseconds(queryParams.endTimeToClose),
      stateDetail: toStateDetailList(queryParams.stateDetail),
    },
    timeout: 30000,
  })
  return data
}

export const MISSION_SPECIALIST_RESOLVED_TASK_KEY = 'mission-specialist-resolved-task'
export const MISSION_SPECIALIST_RESOLVED_TASK_INFINITE_KEY = `${MISSION_SPECIALIST_RESOLVED_TASK_KEY}-infinite`

export function useGetResolved(params: ResolvedTasksRequest) {
  return useQuery<ResolvedTasksResponse>({
    queryKey: [MISSION_SPECIALIST_RESOLVED_TASK_KEY, params],
    queryFn: () => request(params, params.cursor),
    enabled:
      (Boolean(params?.operatorId) || Boolean(params?.programId)) &&
      Boolean(params?.startCreatedDate) &&
      Boolean(params?.endCreatedDate) &&
      Boolean(params?.limit === 10),
    refetchOnWindowFocus: false,
  })
}

export function useGetResolvedWithInfiniteQuery(params: ResolvedTasksRequest) {
  const { cursor, ...otherParams } = params

  const data = useInfiniteQuery<ResolvedTasksResponse>({
    queryKey: [MISSION_SPECIALIST_RESOLVED_TASK_INFINITE_KEY, otherParams],
    queryFn: ({ pageParam = 0 }) => request({ ...params, cursor }, Number(pageParam)),
    enabled:
      (Boolean(params?.operatorId) || Boolean(params?.programId)) &&
      Boolean(params?.startCreatedDate) &&
      Boolean(params?.endCreatedDate) &&
      Boolean(params?.limit === 16),
    refetchOnWindowFocus: false,
    initialPageParam: 0,
    getNextPageParam: () => {
      return params.cursor + params.limit
    },
  })

  const hasPages = data?.data?.pages && data?.data?.pages.length > 0
  const total = hasPages ? data.data?.pages[0].total || 0 : 0

  const allItems = data?.data?.pages.flatMap((page) => page.resolvedTasks) ?? []

  return {
    ...data,
    total,
    items: sortBy(allItems, (item) => new Date(item.createdDate).getTime()).reverse(),
  }
}
